import { useState } from 'react';
import { CpmButton, CpmChip, CpmIconButton } from '../../../components/design-system';
import { CONFIG_TEMPLATE_COPY } from '../configTemplateCopy';
import SettingsInput, { PlusIcon } from './SettingsInput';

export interface ExpenseCategoryRow {
    category: string;
    subcategories: string[];
}

function CloseIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
        </svg>
    );
}

interface ExpenseCategoryListProps {
    value: ExpenseCategoryRow[];
    onChange: (next: ExpenseCategoryRow[]) => void;
    disabled?: boolean;
}

export default function ExpenseCategoryList({
    value,
    onChange,
    disabled = false,
}: ExpenseCategoryListProps) {
    const rows = Array.isArray(value) ? value : [];
    const [categoryDraft, setCategoryDraft] = useState('');
    const [subDrafts, setSubDrafts] = useState<Record<number, string>>({});

    const addCategory = () => {
        const name = categoryDraft.trim();
        if (!name || disabled) return;
        onChange([...rows, { category: name, subcategories: [] }]);
        setCategoryDraft('');
    };

    const addSub = (index: number) => {
        const name = (subDrafts[index] ?? '').trim();
        if (!name || disabled) return;
        onChange(
            rows.map((item, i) =>
                i === index ? { ...item, subcategories: [...item.subcategories, name] } : item
            )
        );
        setSubDrafts((prev) => ({ ...prev, [index]: '' }));
    };

    return (
        <div>
            {rows.map((row, index) => (
                <div
                    key={`${row.category}-${index}`}
                    style={{
                        border: '1px solid var(--cpm-border-default)',
                        borderRadius: 'var(--cpm-radius-8)',
                        padding: '9px 10px',
                        marginBottom: 8,
                    }}
                >
                    <div className="flex items-center" style={{ gap: 8 }}>
                        <span
                            style={{
                                fontSize: 13,
                                fontWeight: 800,
                                color: 'var(--cpm-text-primary)',
                            }}
                        >
                            {row.category || 'Untitled category'}
                        </span>
                        <div className="flex-1" />
                        <CpmIconButton
                            icon={<CloseIcon />}
                            label={CONFIG_TEMPLATE_COPY.REMOVE_CATEGORY}
                            size="sm"
                            tone="danger"
                            disabled={disabled}
                            onClick={() => onChange(rows.filter((_, i) => i !== index))}
                        />
                    </div>
                    <div className="flex flex-wrap" style={{ gap: 6, marginTop: 7 }}>
                        {row.subcategories.map((sub, subIndex) => (
                            <CpmChip
                                key={`${sub}-${subIndex}`}
                                variant="token"
                                onRemove={() =>
                                    onChange(
                                        rows.map((item, i) =>
                                            i === index
                                                ? {
                                                      ...item,
                                                      subcategories: item.subcategories.filter(
                                                          (_, j) => j !== subIndex
                                                      ),
                                                  }
                                                : item
                                        )
                                    )
                                }
                            >
                                {sub}
                            </CpmChip>
                        ))}
                        {row.subcategories.length === 0 ? (
                            <span
                                style={{
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    color: 'var(--cpm-text-tertiary)',
                                }}
                            >
                                {CONFIG_TEMPLATE_COPY.NO_SUBCATEGORIES}
                            </span>
                        ) : null}
                    </div>
                    <div className="flex items-center" style={{ gap: 8, marginTop: 8 }}>
                        <SettingsInput
                            value={subDrafts[index] ?? ''}
                            disabled={disabled}
                            placeholder="Add a subcategory"
                            onChange={(next) =>
                                setSubDrafts((prev) => ({ ...prev, [index]: next }))
                            }
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    addSub(index);
                                }
                            }}
                            width={220}
                        />
                        <CpmButton
                            variant="outline"
                            size="sm"
                            leadingIcon={<PlusIcon />}
                            disabled={disabled || !(subDrafts[index] ?? '').trim()}
                            onClick={() => addSub(index)}
                        >
                            {CONFIG_TEMPLATE_COPY.ADD}
                        </CpmButton>
                    </div>
                </div>
            ))}
            {rows.length === 0 ? (
                <div
                    style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: 'var(--cpm-text-secondary)',
                        marginBottom: 8,
                    }}
                >
                    {CONFIG_TEMPLATE_COPY.EMPTY_CATEGORIES}
                </div>
            ) : null}
            <div className="flex items-center" style={{ gap: 8 }}>
                <SettingsInput
                    value={categoryDraft}
                    disabled={disabled}
                    placeholder="Add a category"
                    onChange={setCategoryDraft}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            addCategory();
                        }
                    }}
                    width={240}
                />
                <CpmButton
                    variant="outline"
                    size="sm"
                    leadingIcon={<PlusIcon />}
                    disabled={disabled || !categoryDraft.trim()}
                    onClick={addCategory}
                >
                    {CONFIG_TEMPLATE_COPY.ADD_CATEGORY}
                </CpmButton>
            </div>
        </div>
    );
}
